import "primeicons/primeicons.css";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useDispatch, useSelector } from "react-redux";
import { Image } from "primereact/image";
import { Tooltip } from "primereact/tooltip";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ICONS from "../Constants/Icons";
import STRINGS from "../Constants/Strings";
import useAccessToken from "../Custom/useAccessToken";
import { RecentArchieveConversation } from "../Models/ConversationApiState";
import { formatConversationListDate, generateGuid } from "../Utils/Common";
import EditConversationTitlePopup from "./EditConversationTitlePopup";
import { AppDispatch } from "../Redux/Store";
import { setHeaderFileNameTitle, setHeaderQuestionTitle } from "../Redux/Header/HeaderActions";
import { closeRightSidebar } from "../Redux/RightSidebar/RightSidebarActions";
import { hideConversationPane, setDefaultConversation } from "../Redux/ChatConversation/ChatConversationActions";
import { toggleUseExternalDocumentsModeFalse } from "../Redux/Dashboard/DashboardActions";

type FilteredConversations = {
  [key: string]: RecentArchieveConversation[];
};

type ConversationsProps = {
  type: "recent" | "archived";
  conversations: RecentArchieveConversation[];
  onMarkRecentAndArchieveClick: (conversation: RecentArchieveConversation, type: string, token: string | null) => void;
  onEditTitle: (conversation: RecentArchieveConversation, value: boolean, token: string | null) => void;
  filterLabels?: string[];
};

const Conversations: React.FC<ConversationsProps> = ({ type, conversations, onMarkRecentAndArchieveClick, onEditTitle }) => {
  const [filteredConversations, setFilteredConversations] = useState<FilteredConversations>({});
  const [selectedConversation, setSelectedConversation] = useState<RecentArchieveConversation | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | number[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const { accessToken } = useAccessToken();
  const navigate = useNavigate();
  const icons = ICONS();

  const filterDataByDate = (
    data: RecentArchieveConversation[],
    filter: string
  ): RecentArchieveConversation[] => {
    const currentDate = new Date();
    const startOfToday = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfToday.getDate() - 1);
    const startOf7DaysAgo = new Date(startOfToday);
    startOf7DaysAgo.setDate(startOfToday.getDate() - 7);
    const startOf30DaysAgo = new Date(startOfToday);
    startOf30DaysAgo.setDate(startOfToday.getDate() - 30);

    return data
      .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
      .filter((item) => {
        const itemDate = new Date(item.updateDate);
        switch (filter) {
          case STRINGS.RECENT.TODAY:
            return itemDate >= startOfToday;
          case STRINGS.RECENT.PREVIOUS_SEVEN_DAYS:
            return itemDate >= startOf7DaysAgo && itemDate < startOfToday;
          case STRINGS.RECENT.PREVIOUS_THIRTY_DAYS:
            return itemDate >= startOf30DaysAgo && itemDate < startOf7DaysAgo;
          case STRINGS.RECENT.OLDER:
            return itemDate < startOf30DaysAgo;
          default:
            return true;
        }
      });
  };

  useEffect(() => {
    setFilteredConversations({});
  }, [type]);

  useEffect(() => {
    const todayData = filterDataByDate(conversations, STRINGS.RECENT.TODAY);

    const previousSevenDaysData = filterDataByDate(conversations, STRINGS.RECENT.PREVIOUS_SEVEN_DAYS).filter(
      (item) => !todayData.some((todayItem) => todayItem.id === item.id)
    );

    const previousThirtyDaysData = filterDataByDate(conversations, STRINGS.RECENT.PREVIOUS_THIRTY_DAYS).filter(
      (item) =>
        !todayData.some((todayItem) => todayItem.id === item.id) && !previousSevenDaysData.some((sevenDaysItem) => sevenDaysItem.id === item.id)
    );

    const olderData = filterDataByDate(conversations, STRINGS.RECENT.OLDER).filter(
      (item) =>
        !todayData.some((todayItem) => todayItem.id === item.id) &&
        !previousSevenDaysData.some((sevenDaysItem) => sevenDaysItem.id === item.id) &&
        !previousThirtyDaysData.some((thirtyDaysItem) => thirtyDaysItem.id === item.id)
    );

    const filteredData: any = {};
    if (todayData && todayData.length > 0) {
      filteredData[STRINGS.RECENT.TODAY] = todayData;
    }
    if (previousSevenDaysData && previousSevenDaysData.length > 0) {
      filteredData[STRINGS.RECENT.PREVIOUS_SEVEN_DAYS] = previousSevenDaysData;
    }
    if (previousThirtyDaysData && previousThirtyDaysData.length > 0) {
      filteredData[STRINGS.RECENT.PREVIOUS_THIRTY_DAYS] = previousThirtyDaysData;
    }
    if (olderData && olderData.length > 0) {
      filteredData[STRINGS.RECENT.OLDER] = olderData;
    }

    // Create an array of keys (tab names) from filteredData
    const activeTabs = Object.keys(filteredData).slice(0, 2);
    setActiveIndex(activeTabs.map((tab) => Object.keys(filteredData).indexOf(tab)));
    setFilteredConversations(filteredData);
  }, [conversations, type]);

  const handleEditClick = (conversation: RecentArchieveConversation) => {
    setSelectedConversation(conversation);
  };

  const handleSaveTitle = (conversation: RecentArchieveConversation) => {
    const isArchived = type === "archived";
    onEditTitle(conversation, isArchived, accessToken);
  };

  const onConversationClick = (conversation: RecentArchieveConversation) => {
    const emptyGuid = "00000000-0000-0000-0000-000000000000";
    const { skillId, skillCode, type: convType, id } = conversation;

    const basePath = window.location.pathname;
    const paths = {
      chatbot: `/app/${skillCode}/${id}`,
      skill: `/app/${skillCode}/${id}`,
      chat: `/chat/${id}`,
    };

    const navigateToPath = (path: string) => {
      if (basePath === path) return;
      dispatch(closeRightSidebar());
      dispatch(hideConversationPane());
      dispatch(setDefaultConversation());
      dispatch(setHeaderQuestionTitle(""));
      dispatch(setHeaderFileNameTitle(""));
      dispatch(toggleUseExternalDocumentsModeFalse());
      navigate(path);
    };

    if (!skillId || skillId === emptyGuid) {
      navigateToPath(paths.chat);
      return;
    }

    switch (convType) {
      case 1:
        navigateToPath(paths.chatbot);
        break;
      case 2:
        navigateToPath(paths.chatbot);
        break;
      case 0:
        navigateToPath(paths.skill);
        break;
      default:
        dispatch(setHeaderQuestionTitle(conversation.title))
        navigateToPath(paths.chat);
        break;
    }
  };

  const onTabIndexChange = (index: number | number[]) => {
    setActiveIndex(index);
  };

  return (
    <div className={`conversations ${type}-conversations`}>
      <Accordion
        multiple
        expandIcon="pi pi-sort-up-fill"
        collapseIcon="pi pi-sort-down-fill"
        activeIndex={activeIndex}
        onTabChange={(e) => onTabIndexChange(e.index)}
      >
        {Object.keys(filteredConversations).map((filter, index) => (
          <AccordionTab key={filter} header={filter}>
            {filteredConversations[filter].length === 0 ? (
              <p className="empty-message">{STRINGS.RECENT.NO_CONVERSATIONS_FOUND}</p>
            ) : (
              filteredConversations[filter].map((conversation) => (
                <div
                  key={conversation.id}
                  id="card"
                  className="card conversation-tab mb-3 text-500"
                  data-pr-tooltip={formatConversationListDate(conversation.updateDate)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConversationClick(conversation);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Tooltip target=".edit" position="right" />
                  <Tooltip target="#card" position="top" />

                  <div className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center position-relative">
                      <div>
                        <p className="text-sm font-medium text-800">{conversation.title}</p>
                      </div>
                      <div className="action-buttons">
                        <Image
                          src={icons.EditConversationTitle}
                          className="edit"
                          data-pr-tooltip="Edit conversation title"
                          alt="Edit Conversation Title Icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(conversation);
                          }}
                        />

                        {type === "recent" && (
                          <>
                            <Image
                              src={icons.SidebarArchieveConversationImg}
                              className="archieve"
                              alt="Archive Icon"
                              data-pr-tooltip="Archive conversation"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMarkRecentAndArchieveClick(conversation, type, accessToken);
                              }}
                            />{" "}
                            <Tooltip target=".archieve" position="right" />
                          </>
                        )}
                        {type === "archived" && (
                          <>
                            <Image
                              src={icons.RemoveFromArchive}
                              className="archieveremoved"
                              alt="Remove from Archive Icon"
                              data-pr-tooltip="Remove from archive"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMarkRecentAndArchieveClick(conversation, type, accessToken);
                              }}
                            />
                            <Tooltip target=".archieveremoved" position="right" />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <small className="mt-2 text-secondary">
                    {conversation.skillId && conversation.skillId !== "00000000-0000-0000-0000-000000000000"
                      ? conversation.indexKey
                      : `${conversation.indexKey} ${conversation.indexValue}`}
                  </small>
                </div>
              ))
            )}
          </AccordionTab>
        ))}
      </Accordion>

      {selectedConversation && (
        <EditConversationTitlePopup
          visible={!!selectedConversation}
          conversation={selectedConversation}
          onHide={() => setSelectedConversation(null)}
          onSave={handleSaveTitle}
        />
      )}
    </div>
  );
};

export default Conversations;
